import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Lock, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import TopBar from "../components/layout/TopBar";
import ContentCreator from "../components/ContentCreator";
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/plans";

const SECTIONS = [
  { key: "ready", title: "Ready to use", desc: "NicheCom content that can immediately become social posts." },
  { key: "recommended", title: "Recommended", desc: "Content Growth believes you should create." },
  { key: "local", title: "Local", desc: "Local and community content opportunities." },
  { key: "evergreen", title: "Evergreen", desc: "Valuations, reviews, team and market knowledge." },
  { key: "seasonal", title: "Seasonal", desc: "Timely content opportunities." },
];

export default function Content() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data } = useApi("/content");
  const [active, setActive] = useState(null);
  const canCreate = can(user?.tier || "score", "content-create");

  return (
    <>
      <TopBar />
      <div className="p-6 sm:p-8 max-w-[1400px]">
        <div className="mb-6 animate-rise">
          <h1 className="text-3xl font-extrabold tracking-tighter text-ink">Content</h1>
          <p className="text-ink-2 mt-1">Turn available content into posts that grow your brand.</p>
        </div>
        {!canCreate && (
          <div className="card p-4 mb-6 flex items-center gap-3" style={{ background: "rgba(147,192,32,.05)" }} data-testid="content-locked-banner">
            <Lock size={16} strokeWidth={1.75} color="#678A16" />
            <p className="text-sm text-ink-2 flex-1"><span className="font-semibold text-ink">Content creation is a Schedule feature.</span> You can browse every opportunity — unlock Schedule to generate and publish posts.</p>
            <button className="btn-primary text-sm focus-ring" onClick={() => navigate("/app/settings")} data-testid="content-upgrade">Unlock Schedule <ArrowRight size={14} strokeWidth={2} /></button>
          </div>
        )}

        {SECTIONS.map((s) => {
          const items = data?.categories?.[s.key] || [];
          if (!items.length) return null;
          return (
            <section key={s.key} className="mb-10">
              <div className="flex items-baseline gap-3 mb-4">
                <h2 className="text-xl font-bold tracking-tight text-ink">{s.title}</h2>
                <span className="text-sm text-ink-2">{s.desc}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {items.map((o, i) => (
                  <motion.div key={o.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                    className="card card-hover overflow-hidden flex flex-col" data-testid={`content-card-${o.id}`}>
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={o.image} alt={o.title} className="w-full h-full object-cover" />
                      <span className="absolute top-2.5 left-2.5 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-white/95 text-ink">{o.type}</span>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="font-semibold text-ink leading-snug">{o.title}</h3>
                      <p className="text-sm text-ink-2 mt-1 flex-1">{o.detail}</p>
                      <button className="mt-3 text-sm font-semibold inline-flex items-center gap-1.5 hover:gap-2 transition-all self-start disabled:opacity-40" style={{ color: "#151515" }}
                        onClick={() => setActive(o)} disabled={!canCreate} data-testid={`create-post-${o.id}`}>
                        <Sparkles size={14} strokeWidth={1.75} color="#678A16" /> Create post
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <AnimatePresence>
        {active && <ContentCreator asset={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </>
  );
}
